import { NetworkStatus } from '@knocklabs/client'
import { useNotificationStore } from '@knocklabs/react-core'
import { type InboxItem, isInboxItem } from '@seen/core/models/inbox'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useKnockDoneFeedClient } from '#src/hooks/useKnockDoneFeedClient.ts'
import {
  KNOCK_PAGE_SIZE,
  type NotificationItem,
} from '#src/hooks/useNotificationItems.ts'

const VISIBILITY_REFETCH_COOLDOWN_MS = 5_000

interface UseDoneNotificationItemsResult {
  items: NotificationItem[]
  loading: boolean
  hasMore: boolean
  isLoadingMore: boolean
  fetchNextPage: () => void
}

export function useDoneNotificationItems(): UseDoneNotificationItemsResult {
  const feedClient = useKnockDoneFeedClient()

  useEffect(() => {
    void feedClient.fetch({ page_size: KNOCK_PAGE_SIZE })
  }, [feedClient])

  /* Prepend any newer notifications on tab focus without resetting loaded pages. */
  const lastRefetchRef = useRef(0)
  useEffect(() => {
    function handleVisibilityChange() {
      if (document.visibilityState !== 'visible') {
        return
      }
      const now = Date.now()
      if (now - lastRefetchRef.current < VISIBILITY_REFETCH_COOLDOWN_MS) {
        return
      }
      lastRefetchRef.current = now
      const { items } = feedClient.store.getState()
      const currentHead = items[0]
      if (currentHead) {
        void feedClient.fetch({ before: currentHead.__cursor })
      } else {
        void feedClient.fetch({ page_size: KNOCK_PAGE_SIZE })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [feedClient])

  const {
    items: feedItems,
    loading,
    pageInfo,
    networkStatus,
  } = useNotificationStore(feedClient)

  const items = useMemo(
    () =>
      feedItems
        .filter(
          (feedItem) => !!feedItem.archived_at && isInboxItem(feedItem.data)
        )
        .map((feedItem) => ({
          ...feedItem,
          data: feedItem.data as InboxItem,
        })),
    [feedItems]
  )

  const hasMore = pageInfo.after !== null
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore

  const fetchNextPage = useCallback(() => {
    void feedClient.fetchNextPage()
  }, [feedClient])

  return { items, loading, hasMore, isLoadingMore, fetchNextPage }
}
